import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type {
  ActionLogEntry,
  MessageRole,
  ProviderConfig,
  TerminalConfig,
  TerminalMessage,
} from "../types";
import type { ChatMessage, LLMProvider, ToolSchema } from "../llm/types";
import { createProvider, providerSupportsTools } from "../llm/registry";
import { ProviderHttpError } from "../llm/stream";
import {
  resolveActiveProvider,
  resolvePersist,
  resolveSystemPrompt,
  TERMINAL_DEFAULTS,
} from "../config/defaults";
import {
  loadState,
  saveState,
  type PersistedState,
} from "../config/persistence";
import { dispatchTerminalAction, subscribeActions } from "../redux/actionBridge";
import {
  CommandRegistry,
  isCommand,
  parseCommand,
} from "../commands/registry";
import { BUILTIN_COMMANDS } from "../commands/builtins";
import type {
  CommandHostApi,
  ProviderSummary,
  TerminalCommand,
} from "../commands/types";
import { makeId } from "../util/id";

const MAX_TOOL_ITERATIONS = 6;

export interface TerminalController {
  messages: TerminalMessage[];
  actionFeed: ActionLogEntry[];
  isOpen: boolean;
  busy: boolean;
  panelHeight: number;
  title: string;
  placeholder: string;
  providers: ProviderSummary[];
  activeProviderId: string | undefined;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setPanelHeight: (px: number) => void;
  setActiveProvider: (id: string) => void;
  clearMessages: () => void;
  submit: (text: string) => void;
  abort: () => void;
  host: CommandHostApi;
}

export interface UseTerminalControllerOptions {
  config: TerminalConfig;
  /** Extra commands merged on top of the built-in set. */
  commands?: TerminalCommand[];
}

export function useTerminalController(
  options: UseTerminalControllerOptions,
): TerminalController {
  const { config } = options;
  const persist = useMemo(() => resolvePersist(config.persist), [config.persist]);

  const [persisted] = useState<PersistedState>(() =>
    persist && persist.namespace ? loadState(persist.namespace) : {},
  );

  // Providers with any persisted API keys merged back in.
  const effectiveProviders = useMemo<ProviderConfig[]>(() => {
    const keys = persist?.persistApiKeys ? persisted.apiKeys : undefined;
    if (!keys) return config.providers;
    return config.providers.map((p) =>
      "apiKey" in p && keys[p.id] ? { ...p, apiKey: keys[p.id] } : p,
    );
  }, [config.providers, persist, persisted.apiKeys]);

  const [activeProviderId, setActiveProviderId] = useState<string | undefined>(
    () =>
      resolveActiveProvider(
        config.providers,
        persisted.activeProviderId ?? config.activeProviderId,
      )?.id,
  );

  const [messages, setMessages] = useState<TerminalMessage[]>(() => {
    if (persist?.persistHistory && persisted.messages?.length) {
      return persisted.messages;
    }
    if (config.greeting) {
      return [
        {
          id: makeId("msg"),
          role: "system",
          content: config.greeting,
          createdAt: Date.now(),
        },
      ];
    }
    return [];
  });

  const [isOpen, setIsOpen] = useState(
    config.defaultOpen ?? TERMINAL_DEFAULTS.defaultOpen,
  );
  const [panelHeight, setPanelHeight] = useState(
    persisted.panelHeight ?? config.panelHeight ?? TERMINAL_DEFAULTS.panelHeight,
  );
  const [busy, setBusy] = useState(false);
  const [actionFeed, setActionFeed] = useState<ActionLogEntry[]>([]);

  // Mutable mirrors read inside async callbacks (avoid stale closures).
  const historyRef = useRef<ChatMessage[]>(
    seedHistory(persist?.persistHistory ? persisted.messages : undefined),
  );
  const abortRef = useRef<AbortController | null>(null);
  const busyRef = useRef(false);
  const configRef = useRef(config);
  configRef.current = config;

  const activeProvider = useMemo(
    () => resolveActiveProvider(effectiveProviders, activeProviderId),
    [effectiveProviders, activeProviderId],
  );
  const providerRef = useRef<{
    provider: LLMProvider;
    config: ProviderConfig;
  } | null>(null);
  providerRef.current = activeProvider
    ? { provider: createProvider(activeProvider), config: activeProvider }
    : null;

  // Subscribe to the Redux action feed for this channel.
  useEffect(() => {
    const channel = config.redux?.channel ?? "default";
    if (config.redux?.logActions === false) return;
    return subscribeActions(channel, (entry) => {
      setActionFeed((prev) => [...prev.slice(-99), entry]);
    });
  }, [config.redux?.channel, config.redux?.logActions]);

  // Persist on change.
  useEffect(() => {
    if (!persist || !persist.namespace) return;
    const state: PersistedState = {
      activeProviderId,
      panelHeight,
      messages: persist.persistHistory ? messages : undefined,
    };
    if (persist.persistApiKeys) {
      state.apiKeys = {};
      for (const p of config.providers) {
        if ("apiKey" in p && p.apiKey) state.apiKeys[p.id] = p.apiKey;
      }
    }
    saveState(persist.namespace, state);
  }, [messages, activeProviderId, panelHeight, persist, config.providers]);

  const print = useCallback(
    (content: string, role: MessageRole = "system") => {
      setMessages((prev) =>
        capHistory(
          [
            ...prev,
            { id: makeId("msg"), role, content, createdAt: Date.now() },
          ],
          configRef.current.maxHistory,
        ),
      );
    },
    [],
  );

  const clearMessages = useCallback(() => {
    historyRef.current = [];
    setMessages([]);
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    busyRef.current = false;
    setBusy(false);
  }, []);

  // ----- Command host API -----
  const host = useMemo<CommandHostApi>(
    () => ({
      get config() {
        return configRef.current;
      },
      listProviders: () =>
        effectiveProviders.map((p) => ({
          id: p.id,
          label: p.label ?? p.id,
          kind: p.kind,
          model: p.model,
          active: p.id === activeProviderId,
          supportsTools: providerSupportsTools(p),
        })),
      setActiveProvider: (id: string) => {
        if (!effectiveProviders.some((p) => p.id === id)) return false;
        setActiveProviderId(id);
        return true;
      },
      clearMessages,
      listActions: () =>
        Object.entries(configRef.current.redux?.actions ?? {}).map(
          ([name, spec]) => ({
            name,
            description: spec.description ?? "(no description)",
          }),
        ),
      dispatchAction: (name, arg) => {
        const spec = configRef.current.redux?.actions?.[name];
        if (!spec) return { ok: false, message: `Unknown action "${name}".` };
        try {
          const action = spec.create(arg);
          const dispatched = dispatchTerminalAction(
            configRef.current.redux?.store,
            action,
          );
          return dispatched
            ? { ok: true, message: `Dispatched \`${action.type}\`.` }
            : {
                ok: false,
                message: `Action built but no \`redux.store\` is wired to dispatch it.`,
              };
        } catch (err) {
          return {
            ok: false,
            message: `Failed to build "${name}": ${errText(err)}`,
          };
        }
      },
      getActionFeed: () => actionFeed,
      abort,
    }),
    [effectiveProviders, activeProviderId, actionFeed, clearMessages, abort],
  );

  const commandRegistry = useMemo(
    () => new CommandRegistry().registerAll(BUILTIN_COMMANDS).registerAll(options.commands ?? []),
    [options.commands],
  );

  // ----- Tool schemas exposed to the LLM -----
  const buildTools = useCallback((): ToolSchema[] | undefined => {
    const redux = configRef.current.redux;
    if (!redux?.actions || redux.enableActionTools === false) return undefined;
    const tools = Object.entries(redux.actions).map(([name, spec]) => ({
      name,
      description: spec.description ?? `Dispatch the ${name} Redux action.`,
      parameters:
        spec.schema ??
        ({ type: "object", properties: {}, additionalProperties: true } as Record<
          string,
          unknown
        >),
    }));
    return tools.length ? tools : undefined;
  }, []);

  const runAssistant = useCallback(
    async (assistantId: string) => {
      const entry = providerRef.current;
      if (!entry) {
        finalize(setMessages, assistantId, "No provider configured.", true);
        return;
      }
      const { provider, config: pcfg } = entry;
      const controller = new AbortController();
      abortRef.current = controller;

      const system = resolveSystemPrompt(configRef.current, pcfg);
      const tools = provider.supportsTools ? buildTools() : undefined;
      const baseReq = {
        model: pcfg.model,
        maxTokens: pcfg.maxTokens ?? 1024,
        system,
        messages: historyRef.current,
        tools,
      };

      try {
        if (tools && provider.supportsTools) {
          await runToolLoop(provider, baseReq, controller.signal, {
            assistantId,
            setMessages,
            host,
          });
        } else {
          let acc = "";
          for await (const chunk of provider.stream(baseReq, controller.signal)) {
            if (chunk.type === "text") {
              acc += chunk.text;
              updateContent(setMessages, assistantId, acc);
            }
          }
          historyRef.current.push({ role: "assistant", content: acc });
          finalize(setMessages, assistantId, acc || "(empty response)");
        }
      } catch (err) {
        if (controller.signal.aborted) {
          finalize(setMessages, assistantId, "(cancelled)", true);
        } else {
          finalize(setMessages, assistantId, errText(err), true);
        }
      } finally {
        abortRef.current = null;
        busyRef.current = false;
        setBusy(false);
      }
    },
    [buildTools, host],
  );

  const submit = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text || busyRef.current) return;

      if (isCommand(text)) {
        const { name, args, argv } = parseCommand(text);
        print(text, "user");
        const command = commandRegistry.get(name);
        if (!command) {
          print(`Unknown command "/${name}". Try /help.`);
          return;
        }
        void Promise.resolve(
          command.run({ args, argv, print, host }),
        ).catch((err) => print(`Command failed: ${errText(err)}`));
        return;
      }

      // Regular chat turn.
      print(text, "user");
      historyRef.current.push({ role: "user", content: text });

      const assistantId = makeId("msg");
      setMessages((prev) =>
        capHistory(
          [
            ...prev,
            {
              id: assistantId,
              role: "assistant",
              content: "",
              createdAt: Date.now(),
              streaming: true,
              source: providerRef.current?.config.id,
            },
          ],
          configRef.current.maxHistory,
        ),
      );
      busyRef.current = true;
      setBusy(true);
      void runAssistant(assistantId);
    },
    [commandRegistry, host, print, runAssistant],
  );

  return {
    messages,
    actionFeed,
    isOpen,
    busy,
    panelHeight,
    title: config.title ?? TERMINAL_DEFAULTS.title,
    placeholder: config.placeholder ?? TERMINAL_DEFAULTS.placeholder,
    providers: host.listProviders(),
    activeProviderId,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((v) => !v),
    setPanelHeight,
    setActiveProvider: (id) => setActiveProviderId(id),
    clearMessages,
    submit,
    abort,
    host,
  };
}

// ----- helpers -----

async function runToolLoop(
  provider: LLMProvider,
  baseReq: {
    model: string;
    maxTokens: number;
    system?: string;
    messages: ChatMessage[];
    tools?: ToolSchema[];
  },
  signal: AbortSignal,
  ctx: {
    assistantId: string;
    setMessages: Dispatch<SetStateAction<TerminalMessage[]>>;
    host: CommandHostApi;
  },
): Promise<void> {
  let narration = "";
  for (let i = 0; i < MAX_TOOL_ITERATIONS; i++) {
    const result = await provider.complete(
      { ...baseReq, messages: baseReq.messages },
      signal,
    );

    if (!result.toolCalls.length) {
      baseReq.messages.push({ role: "assistant", content: result.text });
      finalize(ctx.setMessages, ctx.assistantId, narration + result.text);
      return;
    }

    // Record the assistant's tool-call turn.
    baseReq.messages.push({
      role: "assistant",
      content: result.text,
      toolCalls: result.toolCalls,
    });
    if (result.text) narration += result.text + "\n";

    for (const call of result.toolCalls) {
      const outcome = ctx.host.dispatchAction(call.name, call.arguments);
      narration += `⚙ ${call.name}(${compact(call.arguments)}) — ${
        outcome.ok ? "ok" : outcome.message
      }\n`;
      updateContent(ctx.setMessages, ctx.assistantId, narration);
      baseReq.messages.push({
        role: "tool",
        toolCallId: call.id,
        toolName: call.name,
        content: outcome.message,
      });
    }
  }
  finalize(
    ctx.setMessages,
    ctx.assistantId,
    narration + "\n(stopped: reached tool-call limit)",
    true,
  );
}

function updateContent(
  setMessages: Dispatch<SetStateAction<TerminalMessage[]>>,
  id: string,
  content: string,
): void {
  setMessages((prev) =>
    prev.map((m) => (m.id === id ? { ...m, content } : m)),
  );
}

function finalize(
  setMessages: Dispatch<SetStateAction<TerminalMessage[]>>,
  id: string,
  content: string,
  error = false,
): void {
  setMessages((prev) =>
    prev.map((m) =>
      m.id === id ? { ...m, content, streaming: false, error } : m,
    ),
  );
}

function seedHistory(messages?: TerminalMessage[]): ChatMessage[] {
  if (!messages) return [];
  return messages
    .filter((m) => m.role !== "system" && !m.error && m.content)
    .map((m) => ({ role: m.role as "user" | "assistant", content: m.content }));
}

function capHistory(
  messages: TerminalMessage[],
  max = TERMINAL_DEFAULTS.maxHistory,
): TerminalMessage[] {
  return messages.length > max ? messages.slice(messages.length - max) : messages;
}

function compact(obj: Record<string, unknown>): string {
  try {
    const s = JSON.stringify(obj);
    return s.length > 60 ? s.slice(0, 60) + "…" : s;
  } catch {
    return "…";
  }
}

function errText(err: unknown): string {
  if (err instanceof ProviderHttpError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}
